import type { HTMLProps, Key, ReactElement, ReactNode } from 'react';
import cx from 'clsx';
import { Modal, ModalOverlay, type ModalOverlayProps } from 'react-aria-components';
import { Button, Tooltip } from '@repo/design-system-kit';
import { IconCrossOutlined } from '@repo/monochrome-icons';
import { useIntl } from 'react-intl';
import { PreviewModalContextProvider, usePreviewModalContext } from './preview-modal.context.tsx';
import styles from './preview-modal.module.css';

interface PreviewModalProps extends ModalOverlayProps {
  onClose: () => void;
}

function PreviewModalComponent({
  className,
  isOpen,
  onClose,
  ...props
}: PreviewModalProps): ReactElement {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;
  return (
    <PreviewModalContextProvider onClose={onClose}>
      <ModalOverlay UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER} isOpen={isOpen}>
        <Modal className={cx(styles.modal, className)} {...props} />
      </ModalOverlay>
    </PreviewModalContextProvider>
  );
}

interface ActionProps {
  key: Key;
  label: ReactNode;
  iconOnly?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  onPress?: () => void;
}

interface PreviewModalHeaderProps extends Omit<HTMLProps<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: string | null;
  actions?: ActionProps[];
}

function PreviewModalHeader({
  title,
  subtitle,
  actions,
  className,
  ...props
}: PreviewModalHeaderProps): ReactElement {
  const { formatMessage } = useIntl();
  const { onClose } = usePreviewModalContext();

  return (
    <header className={cx(styles.header, className)} {...props}>
      <div className={styles.titles}>
        <div className={cx(styles.title, 'title-4')} data-test-page-header-title>
          {title}
        </div>
        <div className={cx(styles.subtitle, 'caption')} data-test-page-header-subtitle>
          {subtitle}
        </div>
      </div>
      <div className={styles.actions}>
        {actions?.map(({ iconOnly, isDisabled, key, label, tooltip, onPress }) => (
          <Tooltip isDisabled={!tooltip} key={key} label={tooltip} placement="bottom end">
            <Button
              aria-label={tooltip}
              data-testid={`preview-action-${key}`}
              iconOnly={iconOnly}
              isDisabled={isDisabled}
              onPress={onPress}
              size="large"
              variant="tertiary"
            >
              {label}
            </Button>
          </Tooltip>
        ))}
        <Tooltip
          label={formatMessage({ id: 'shared-kit.a11y.buttons.close-aria-label' })}
          placement="bottom end"
        >
          <Button
            aria-label={formatMessage({ id: 'shared-kit.a11y.buttons.close-aria-label' })}
            data-testid="preview-action-close"
            iconOnly
            onPress={onClose}
            size="large"
            variant="tertiary"
          >
            <IconCrossOutlined />
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}

function PreviewModalDocument({ className, ...props }: HTMLProps<HTMLDivElement>): ReactElement {
  return <section className={cx(styles.document, className)} {...props} />;
}

function PreviewModalSidePanel({ className, ...props }: HTMLProps<HTMLDivElement>): ReactElement {
  return <aside className={cx(styles.sidePanel, className)} {...props} />;
}

export const PreviewModal = Object.assign(PreviewModalComponent, {
  Header: PreviewModalHeader,
  Document: PreviewModalDocument,
  SidePanel: PreviewModalSidePanel,
});
